import { useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { useAuth } from "../auth.tsx";
import { useAction } from "../api.ts";
import { Button, ErrorText, Field, Input } from "../ui/index.tsx";
import { AuthLayout } from "../layouts.tsx";

const safeNext = (v: string | null) => (v && v.startsWith("/") && !v.startsWith("//") ? v : "/home");

export default function Login() {
  const { me, login, setupPassword } = useAuth();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState(false);

  const signIn = useAction(async () => {
    const r = await login(username.trim(), password);
    if (r.kind === "setup") setSetupToken(r.setupToken);
  });
  const setup = useAction(async () => { await setupPassword(setupToken!, newPassword); });

  if (me) return <Navigate to={next} replace />;

  const onSignIn = (e: FormEvent) => { e.preventDefault(); void signIn.run(); };
  const onSetup = (e: FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirm) { setMismatch(true); return; }
    setMismatch(false);
    void setup.run();
  };

  if (setupToken) {
    return (
      <AuthLayout title="Choose a password"
        subtitle="Your account moved to passwords. Your old license key worked one last time — choose a password."
        footer={<button type="button" className="hover:text-fg" onClick={() => { setSetupToken(null); setPassword(""); }}>Back to sign in</button>}>
        <form onSubmit={onSetup} className="space-y-4">
          <Field label="New password" hint="8 to 128 characters">
            <Input type="password" autoComplete="new-password" autoFocus required minLength={8} maxLength={128} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </Field>
          <Field label="Confirm password" error={mismatch ? "Passwords don't match" : null}>
            <Input type="password" autoComplete="new-password" required minLength={8} maxLength={128} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </Field>
          <ErrorText>{setup.error}</ErrorText>
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={setup.pending}>Set password and sign in</Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to your ExoWare account."
      footer={<>New here? <Link to="/register" className="text-accent hover:underline">Create an account</Link></>}>
      <form onSubmit={onSignIn} className="space-y-4">
        <Field label="Username">
          <Input autoComplete="username" autoFocus required value={username} onChange={(e) => setUsername(e.target.value)} />
        </Field>
        <Field label="Password">
          <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <ErrorText>{signIn.error}</ErrorText>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={signIn.pending}>Sign in</Button>
      </form>
    </AuthLayout>
  );
}
